/**
 * Which of a named list of catalogue cards a person actually owns.
 *
 * This is the ownership question a CHASE BINDER asks, and it is a plainer one
 * than a master set's. A master set's slots are (card, printing) pairs, so the
 * rule behind them has to decide what an unrecorded printing fills and it lives
 * in one place only, `public.wish_set_slot_rows`. A chase binder holds cards,
 * not printings: the only question is whether this person has that catalogue
 * card at all. That is the join phase 4 established - `public.cards.card_id`
 * against the catalogue id, never the `tcgdex_card_id` column that does not
 * exist and made the set page read 0/N for months.
 *
 * WHOSE CARDS ARE COUNTED.
 *
 * The acting profile, passed in, never "whoever is signed in". A parent switched
 * into a child is looking at the CHILD's chase binder, so the gauge has to count
 * the child's cards; phase 5 fixed exactly this on the master set bar. The RLS
 * policy on `public.cards` permits a caller to read their own profiles and their
 * linked children, so an explicit `user_id` filter is both allowed and the whole
 * of what is read - there is no path here to counting a stranger's cards as
 * somebody's own.
 *
 * Soft-deleted rows are excluded, which matches the SQL rule: an owner can see
 * their own deleted cards, and a card in the bin is not a card they have.
 */

import { supabase } from '../lib/supabase';

/** PostgREST .in() URLs stay a sane length, the same chunk the price read uses. */
const CHUNK = 200;

/**
 * @param {string[]} cardIds catalogue ids
 * @param {string} profileId the ACTING profile: self, or a linked child
 * @returns {Promise<Set<string>>} the ids they own
 */
export async function fetchOwnedCardIds(cardIds, profileId) {
  const ids = [...new Set((cardIds || []).filter(Boolean))];
  const owned = new Set();
  if (!profileId || ids.length === 0) return owned;

  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK);
    const { data, error } = await supabase
      .from('cards')
      .select('card_id')
      .eq('user_id', profileId)
      .is('deleted_at', null)
      .in('card_id', chunk);
    if (error) throw error;
    for (const row of data || []) if (row.card_id) owned.add(row.card_id);
  }
  return owned;
}

/**
 * Every chase binder's contents, as a card id list per binder.
 *
 * One read for the lot rather than one per binder, because the Wish tab draws a
 * gauge on every binder row at once and a person may have a dozen of them. The
 * ids are what a gauge needs: its denominator is the length of this list, and
 * its numerator is how many of these ids the acting profile owns.
 *
 * @param {string} userId whose binders
 * @returns {Promise<Record<string, string[]>>} binder id -> catalogue ids
 */
export async function fetchWishBinderCardIds(userId) {
  if (!userId) return {};
  const { data, error } = await supabase
    .from('wish_binder_cards')
    .select('binder_id, tcgdex_card_id')
    .eq('user_id', userId);
  if (error) throw error;
  const byBinder = {};
  for (const row of data || []) {
    if (!row.binder_id) continue;
    if (!byBinder[row.binder_id]) byBinder[row.binder_id] = [];
    if (row.tcgdex_card_id) byBinder[row.binder_id].push(row.tcgdex_card_id);
  }
  return byBinder;
}

/**
 * How full every chase binder is, against its own size.
 *
 * Chase's words: "If they only have ten cards in there or fifty cards, it
 * should be filling up to the amount of cards in that particular custom
 * binder." So a 50-card binder holding 12 owned cards reads 12 of 50, and the
 * denominator is never a set's size, because a chase binder is not a set.
 *
 * `profileId` of null means nobody's ownership is being drawn - somebody else's
 * chase list - and every binder comes back with `owned: null`, which is what
 * the gauge reads as "size, and no bar".
 *
 * @param {string} userId whose binders
 * @param {string|null} profileId the acting profile to count ownership for
 * @returns {Promise<Record<string, { total: number, owned: number|null }>>}
 */
export async function fetchWishBinderProgress(userId, profileId) {
  const byBinder = await fetchWishBinderCardIds(userId);
  const binderIds = Object.keys(byBinder);
  if (!profileId) {
    const out = {};
    for (const id of binderIds) out[id] = { total: byBinder[id].length, owned: null };
    return out;
  }

  const all = binderIds.flatMap((id) => byBinder[id]);
  const owned = await fetchOwnedCardIds(all, profileId);
  const out = {};
  for (const id of binderIds) {
    const cards = byBinder[id];
    out[id] = { total: cards.length, owned: cards.filter((c) => owned.has(c)).length };
  }
  return out;
}
